import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Col, Progress, Row, Statistic, Table, Tag, Tooltip, Typography } from "antd";
import {
  ApiOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DatabaseOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { Button, Card, PageHeader } from "../../components/Common";
import { handleAxiosError } from "../../components/Common/apiHelpers";
import axiosClient from "../../api/axiosClient";
import MaintenanceCard from "./components/MaintenanceCard";

const STATUS_ENDPOINT = "/v1/admin/redis/status";

// Redis holds only rebuildable state, so a stale reading is never dangerous — but it is
// the panel someone stares at during an incident, so it refreshes on its own.
const REFRESH_MS = 15000;

const formatBytes = (bytes) => {
  if (!bytes || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** exponent).toFixed(exponent === 0 ? 0 : 1)} ${units[exponent]}`;
};

const formatUptime = (seconds) => {
  if (!seconds || seconds <= 0) return "—";
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const FEATURES = [
  {
    key: "distributedRateLimiting",
    label: "Distributed rate limiting",
    on: "The published limit is the real one, however many instances run.",
    off: "Each instance counts on its own, so the effective limit is multiplied by the instance count.",
  },
  {
    key: "signalRBackplane",
    label: "SignalR backplane",
    on: "Every connected client sees events published by any instance.",
    off: "A client only sees events from the instance it happens to be connected to.",
  },
  {
    key: "sharedEmbeddingCache",
    label: "Shared embedding cache",
    on: "The same query text is embedded once for the whole platform.",
    off: "Every instance pays the provider again for the same text.",
  },
];

const RedisMonitor = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const mounted = useRef(true);

  const load = useCallback(async (isBackground) => {
    if (isBackground) setRefreshing(true);
    try {
      const { data } = await axiosClient.get(STATUS_ENDPOINT);
      if (mounted.current) setStatus(data?.data ?? null);
    } catch (error) {
      // A background poll that fails must not throw a toast every 15 seconds at someone
      // who is already looking at the screen.
      if (!isBackground) handleAxiosError(error, "Could not read the Redis status.");
    } finally {
      if (mounted.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    load(false);
    const timer = setInterval(() => load(true), REFRESH_MS);
    return () => {
      mounted.current = false;
      clearInterval(timer);
    };
  }, [load]);

  const header = (
    <PageHeader
      title="Redis"
      subtitle="Shared state across API instances — caches, rate-limit counters and real-time fan-out."
      extra={
        <Button
          variant="secondary"
          icon={<ReloadOutlined />}
          loading={refreshing}
          onClick={() => load(false)}
        >
          Refresh
        </Button>
      }
    />
  );

  if (loading) {
    return (
      <>
        {header}
        <Card loading />
      </>
    );
  }

  if (!status?.configured) {
    return (
      <>
        {header}
        <Alert
          type="info"
          showIcon
          message="Redis is not configured"
          description={
            <>
              <p style={{ marginBottom: 8 }}>
                The platform is running in per-instance mode: caches live in each process,
                rate limiting counts locally, and real-time events reach only the clients
                connected to the same instance. <strong>That is correct for a single
                instance</strong> — Redis is what makes a second one correct.
              </p>
              <p style={{ marginBottom: 0 }}>
                To enable it, set <code>Redis:ConnectionString</code> (Actions →
                “Provisiono Redis-in në VPS”, or <code>REDIS_PASSWORD</code> in{" "}
                <code>.env</code> for Docker).
              </p>
            </>
          }
        />
      </>
    );
  }

  const { server, memory, throughput, features, keyGroups = [] } = status;
  const connected = status.connected;

  return (
    <>
      {header}

      {!connected && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Redis is configured but unreachable"
          description="Every feature has fallen back to per-instance behaviour. Requests are unaffected — the API skips Redis instead of waiting on it — but limits are no longer shared and cached vectors are being recomputed. It reconnects on its own; no restart is needed."
        />
      )}

      {connected && status.error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Could not read the full status"
          description={status.error}
        />
      )}

      <Row gutter={[16, 16]} align="stretch">
        <Col xs={24} lg={8}>
          <Card title={<><ApiOutlined /> Connection</>} style={{ height: "100%" }}>
            <Statistic
              title="Status"
              value={connected ? "Connected" : "Unreachable"}
              valueStyle={{ color: connected ? "var(--success, #52c41a)" : "var(--error, #ff4d4f)" }}
              prefix={connected ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
            />
            <div style={{ marginTop: 16, display: "grid", gap: 8 }}>
              <Row justify="space-between">
                <Typography.Text type="secondary">Endpoint</Typography.Text>
                <Typography.Text code>{status.endpoint || "—"}</Typography.Text>
              </Row>
              <Row justify="space-between">
                <Typography.Text type="secondary">Version</Typography.Text>
                <Typography.Text>{server?.version || "—"}</Typography.Text>
              </Row>
              <Row justify="space-between">
                <Typography.Text type="secondary">Uptime</Typography.Text>
                <Typography.Text>{formatUptime(server?.uptimeSeconds)}</Typography.Text>
              </Row>
              <Row justify="space-between">
                <Typography.Text type="secondary">Clients</Typography.Text>
                <Typography.Text>{server?.connectedClients ?? "—"}</Typography.Text>
              </Row>
              <Row justify="space-between">
                <Typography.Text type="secondary">Ping</Typography.Text>
                <Typography.Text>
                  {server ? `${server.latencyMs} ms` : "—"}
                </Typography.Text>
              </Row>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title={<><DatabaseOutlined /> Memory</>} style={{ height: "100%" }}>
            <Statistic title="In use" value={formatBytes(memory?.usedBytes)} />
            {memory?.usedPercent != null ? (
              <Tooltip title={`Ceiling ${formatBytes(memory.maxBytes)}. Past it, keys are evicted by ${memory.evictionPolicy}.`}>
                <Progress
                  percent={Math.min(100, memory.usedPercent)}
                  size="small"
                  status={memory.usedPercent >= 90 ? "exception" : "normal"}
                  style={{ marginTop: 12 }}
                />
              </Tooltip>
            ) : (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 12 }}
                message="No memory ceiling"
                description="Without maxmemory, Redis grows until the host runs out. Set one so it evicts instead."
              />
            )}
            <div style={{ marginTop: 16, display: "grid", gap: 8 }}>
              <Row justify="space-between">
                <Typography.Text type="secondary">Eviction policy</Typography.Text>
                <Typography.Text code>{memory?.evictionPolicy || "—"}</Typography.Text>
              </Row>
              <Row justify="space-between">
                <Tooltip title="Evictions are safe here — everything Redis holds can be rebuilt — but a rising count means the ceiling is too low for the traffic.">
                  <Typography.Text type="secondary">Evicted keys</Typography.Text>
                </Tooltip>
                <Typography.Text>{memory?.evictedKeys ?? 0}</Typography.Text>
              </Row>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title={<><ThunderboltOutlined /> Throughput</>} style={{ height: "100%" }}>
            <Statistic
              title="Cache hit rate"
              value={throughput?.hitRatePercent != null ? throughput.hitRatePercent : "—"}
              suffix={throughput?.hitRatePercent != null ? "%" : ""}
            />
            <div style={{ marginTop: 16, display: "grid", gap: 8 }}>
              <Row justify="space-between">
                <Typography.Text type="secondary">Ops / second</Typography.Text>
                <Typography.Text>{throughput?.opsPerSecond ?? 0}</Typography.Text>
              </Row>
              <Row justify="space-between">
                <Typography.Text type="secondary">Hits / misses</Typography.Text>
                <Typography.Text>
                  {throughput?.keyspaceHits ?? 0} / {throughput?.keyspaceMisses ?? 0}
                </Typography.Text>
              </Row>
              <Row justify="space-between">
                <Typography.Text type="secondary">Expired keys</Typography.Text>
                <Typography.Text>{throughput?.expiredKeys ?? 0}</Typography.Text>
              </Row>
              <Row justify="space-between">
                <Typography.Text type="secondary">Total commands</Typography.Text>
                <Typography.Text>
                  {(throughput?.totalCommands ?? 0).toLocaleString()}
                </Typography.Text>
              </Row>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Cross-instance behaviour" style={{ height: "100%" }}>
            <div style={{ display: "grid", gap: 12 }}>
              {FEATURES.map(({ key, label, on, off }) => {
                const enabled = Boolean(features?.[key]) && connected;
                return (
                  <div key={key}>
                    <Row justify="space-between" align="middle">
                      <Typography.Text strong>{label}</Typography.Text>
                      <Tag color={enabled ? "green" : "default"}>
                        {enabled ? "Active" : "Per-instance"}
                      </Tag>
                    </Row>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {enabled ? on : off}
                    </Typography.Text>
                  </div>
                );
              })}
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Keys by purpose" style={{ height: "100%" }}>
            <Table
              size="small"
              pagination={false}
              rowKey="name"
              dataSource={keyGroups}
              locale={{ emptyText: "No keys yet." }}
              columns={[
                {
                  title: "Group",
                  dataIndex: "name",
                  render: (value, row) => (
                    <Tooltip title={row.description}>
                      <span>{value}</span>
                    </Tooltip>
                  ),
                },
                {
                  title: "Keys",
                  dataIndex: "count",
                  align: "right",
                  render: (value, row) =>
                    row.truncated ? (
                      <Tooltip title="The scan stopped at its cap, so this is a floor rather than a total.">
                        <span>{value.toLocaleString()}+</span>
                      </Tooltip>
                    ) : (
                      value.toLocaleString()
                    ),
                },
              ]}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 12 }}>
              Counted with SCAN, never KEYS — a full scan would block the server for every
              other caller. Rate-limit counters expire on their own each minute.
            </Typography.Text>
          </Card>
        </Col>

        <Col xs={24}>
          <MaintenanceCard connected={connected} onDone={() => load(false)} />
        </Col>
      </Row>
    </>
  );
};

export default RedisMonitor;
